import { Controller } from '@hotwired/stimulus'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import { render } from 'nunjucks'

import { router } from '../router'

import { Events, Strings } from '../constants'

import {
    target,
    retrieveSession,
    sendEvent,
    decodeJson,
    subscribeEvent,
    unsubscribeEvent,
    retrieveState,
    updateState,
    addClass,
    removeClass
} from '../utils'

import { customerServiceCentersPostRequest } from '../api'

export default class extends Controller {

    static targets = [
        'map',
        'my-location',
    ]

    static values = {
    }

    state = {
        session: null,
        centers: []
    }

    instances = {
        loader: null,
        map: null,
        locationWatches: null,
        myLocation: null,
        infoWindow: null,
        markers: [],
        cluster: null
    }

    configs = {
        map: {
            key: Strings.API_KEY,
            libraries: [],
            v: 'quarterly'
        }
    }

    map = () => target(this, 'map')

    myLocation = () => target(this, 'my-location')

    initialize = () => {

        setOptions(this.configs.map);

        this.instances.loader = {
            load: () => importLibrary('maps')
        }
    }

    connect = () => {

        this.instances.loader.load()
            .then(() => this.renderMap())
            .then(() => this.refreshOnLogin())

        subscribeEvent(Events.ON_LOGIN, this.refreshOnLogin)
    }

    disconnect = () => {

        const { geolocation } = navigator

        if (this.instances.locationWatches) {

            geolocation.clearWatch(this.instances.locationWatches)
        }

        unsubscribeEvent(Events.ON_LOGIN, this.refreshOnLogin)
    }

    refreshOnLogin = () => {

        retrieveSession()
            .then((session) => {

                if (! session) {

                    sendEvent(Events.SHOW_LOGIN, { message: '' })

                    return
                }

                this.state.session = session

                return retrieveState()
                    .then((state) => {

                        if (! state || ! state.centers) {

                            this.refresh()

                            return
                        }

                        this.state.centers = state.centers

                        this.renderCenters()
                    })
                    .catch(() => {})
          })
          .catch(() => {

              sendEvent(Events.SHOW_LOGIN, { message: '' })
          })
    }

    refresh = () => {

        if (! this.state.session) {

            return
        }

        const params = {
            rpe: this.state.session.rpe,
            password: this.state.session.password
        }

        customerServiceCentersPostRequest(params)
            .then(this.onRefresh)
            .catch(this.onRefresh)
    }

    onRefresh = (response) => {

        if (response.estado) {

            this.state.centers = decodeJson(response.mensaje)

            this.renderCenters()

            updateState({
                centers: this.state.centers
            })
        }
    }

    renderMap = () => {

        const { maps: { Map, InfoWindow, Marker, SymbolPath: { CIRCLE } } } = google

        this.instances.map = new Map(this.map(), {
            center: {
                lat: 21.8440139,
                lng: -102.275549
            },
            zoom: 6,
            disableDefaultUI: true,
            clickableIcons: false
        })

        this.instances.infoWindow = new InfoWindow()

        this.instances.myLocation = new Marker({
            title: 'Mi ubicación',
            icon: {
                path: CIRCLE,
                fillColor: '#5991f8',
                fillOpacity: 0.95,
                strokeColor: '#ffffff',
                strokeOpacity: 0.95,
                strokeWeight: 2,
                scale: 15
            }
        })
    }

    renderCenters = () => {

        const { maps: { Marker, LatLng, SymbolPath: { BACKWARD_CLOSED_ARROW } } } = google

        this.instances.infoWindow.close()

        this.state.centers.forEach((center) => {

            const marker = new Marker({
                position: new LatLng(parseFloat(center.x), parseFloat(center.y)),
                title: center.nombre,
                icon: {
                    path: BACKWARD_CLOSED_ARROW,
                    fillColor: '#0b4219',
                    fillOpacity: 1.0,
                    strokeColor: '#0b6b25',
                    strokeOpacity: 1.0,
                    strokeWeight: 1,
                    scale: 6
                }
            })

            const callback = this.markerClicked.bind(this, center, marker)

            marker.addListener('click', callback)

            this.instances.markers.push(marker)
        })

        this.instances.cluster = new MarkerClusterer(this.instances.map, this.instances.markers, {
            clusterClass: 'custom-clustericon',
            styles: [
                {
                    width: 20,
                    height: 20,
                    className: 'custom-clustericon-1'
                },
                {
                    width: 30,
                    height: 30,
                    className: 'custom-clustericon-2'
                },
                {
                    width: 40,
                    height: 40,
                    className: 'custom-clustericon-3'
                }
            ]
        })
    }

    markerClicked = (center, marker) => {

        const content = render('window-info-customer-service-center.html', center)

        this.instances.infoWindow.setContent(content)

        this.instances.infoWindow.setPosition(marker.getPosition())

        this.instances.infoWindow.open(this.instances.map)
    }

    openMaps = ({ params }) => {

        if (isNaN(params.x) || isNaN(params.y)) {

            return
        }

        const platform = (navigator.userAgentData || navigator).platform || 'unknown'

        if (platform.indexOf('iPhone') !== -1 ||
                platform.indexOf('iPod') !== -1 ||
                    platform.indexOf('iPad') !== -1) {

            window.open(`maps://maps.google.com/maps?daddr=${params.x},${params.y}`)

        } else {

            window.open(`https://maps.google.com/maps?daddr=${params.x},${params.y}`)
        }
    }

    checkLocation = () => {

        const { geolocation } = navigator

        if (! geolocation) {

            this.errorLocation(false)

            return
        }

        addClass(this.myLocation(), 'is-loading')

        const options = {
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 60000
        }

        geolocation.getCurrentPosition(this.updateLocation, this.errorUpdateLocation, options)

        if (this.instances.locationWatches) {

            geolocation.clearWatch(this.instances.locationWatches)

            this.instances.locationWatches = null
        }

        this.instances.locationWatches = geolocation.watchPosition(
          this.updateLocationInBackground, this.errorLocationInBackground, options)
    }

    updateLocation = ({ coords: { latitude, longitude } }) => {

        const { maps: { LatLng } } = google

        this.instances.map.panTo(new LatLng(latitude, longitude))

        this.instances.map.setZoom(16)
    }

    updateLocationInBackground = ({ coords: { latitude, longitude } }) => {

        const { maps: { LatLng } } = google

        this.instances.myLocation.setMap(this.instances.map)

        this.instances.myLocation.setPosition(new LatLng(latitude, longitude))
    }

    errorUpdateLocation = (error) => {

        this.errorLocation(true, error.code !== 1)
    }

    errorLocation = (browserHasGeolocation, hasPermissions) => {

        const position = this.instances.map.getCenter()

        this.instances.infoWindow.setPosition(position)

        this.instances.infoWindow.setContent(browserHasGeolocation ? (hasPermissions ?
            Strings.ERROR_LOCATION : Strings.ERROR_LOCATION_PERMISSION)  : Strings.ERROR_LOCATION_FEATURE)

        this.instances.infoWindow.open(this.instances.map)

        removeClass(this.myLocation(), 'is-loading')
    }

    errorLocationInBackground = () => {
    }
}
